import {
  ChevronDownIcon,
  ChevronUpIcon,
  } from "@heroicons/react/24/outline";
import { Button, Spinner } from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";
import stripeIcon from "../../assets/settingPage/stripeIcon.svg";
import unlinkIcon from "../../assets/settingPage/unlinkIcon.svg";
import ConfirmModal from "../../components/common/confirmModal";
import {
  connectStripe,
  getConnectDetails,
  unlinkStripe,
} from "../../services/api";
import { StripeAccountStatusDto } from "../../types/types";

type StripeAccountInfo = StripeAccountStatusDto & {
  accountName?: string | null;
  linkedOn?: string | null;
};

const StripeLinkCard: React.FC = () => {
  const { t, i18n } = useTranslation("settings");
  const [accountInfo, setAccountInfo] = useState<StripeAccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpeningOnboarding, setIsOpeningOnboarding] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const loadAccountInfo = async () => {
    try {
      const info = await getConnectDetails();
      setAccountInfo(info);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAccountInfo();
    window.addEventListener("focus", loadAccountInfo);
    return () => window.removeEventListener("focus", loadAccountInfo);
  }, []);

  const openLink = (url: string) => {
    const newTab = window.open(url, "_blank");
    if (!newTab) {
      window.location.href = url;
    }
  };

  const handleStripeClick = async () => {
    setIsOpeningOnboarding(true);

    try {
      const url = await connectStripe();
      if (url && url.startsWith("http")) {
        openLink(url);
      } else {
        await loadAccountInfo();
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          t("failed_to_open_onboarding")
      );
    } finally {
      setIsOpeningOnboarding(false);
    }
  };

  const handleUnlink = async () => {
    setIsUnlinking(true);

    try {
      await unlinkStripe();
      toast.success(t("stripe_unlinked"));
      setIsExpanded(false);
      await loadAccountInfo();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          t("failed_to_unlink_stripe")
      );
    } finally {
      setIsUnlinking(false);
    }
  };

  const connectStatus = accountInfo?.status?.trim().toUpperCase() ?? null;
  const isActive = connectStatus === "ACCOUNT_ACTIVE";
  const isPending = connectStatus === "VERIFICATION_PENDING";

  const bankText =
    accountInfo?.bankName && accountInfo?.bankAccountLast4
      ? `${accountInfo.bankName} •••• ${accountInfo.bankAccountLast4}`
      : accountInfo?.bankName || "";
  const subTitle = accountInfo?.accountName || bankText;

  const linkedDate = accountInfo?.linkedOn ? new Date(accountInfo.linkedOn) : null;
  const linkedDateText =
    linkedDate && !isNaN(linkedDate.getTime())
      ? linkedDate.toLocaleDateString(
          i18n.language === "en" ? "en-GB" : i18n.language,
          { day: "numeric", month: "long", year: "numeric" }
        )
      : "";

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      );
    }

    if (!isActive && !isPending) {
      return (
        <button
          type="button"
          onClick={handleStripeClick}
          disabled={isOpeningOnboarding}
          className="w-32 flex flex-col items-center gap-2 rounded-2xl border border-gray-100 p-3 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-60"
        >
          {isOpeningOnboarding ? (
            <div className="size-12 flex items-center justify-center">
              <Spinner size="sm" />
            </div>
          ) : (
            <img src={stripeIcon} alt="Stripe" className="size-12 rounded-xl" />
          )}
          <span className="text-sm font-bold text-gray-900">Stripe</span>
        </button>
      );
    }

    return (
      <div className="rounded-2xl border border-gray-100">
        <button
          type="button"
          onClick={() => setIsExpanded((previous) => !previous)}
          aria-expanded={isExpanded}
          className="w-full flex items-center gap-4 p-4 text-left"
        >
          <img src={stripeIcon} alt="Stripe" className="size-12 rounded-xl" />
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-gray-900">
              {t("stripe_account")}
            </p>
            {subTitle && (
              <p className="text-sm text-gray-600 truncate">{subTitle}</p>
            )}
          </div>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              isActive
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {isActive ? t("stripe_connected") : t("stripe_verification_pending")}
          </span>
          {isExpanded ? (
            <ChevronUpIcon className="size-5 text-gray-900" />
          ) : (
            <ChevronDownIcon className="size-5 text-gray-900" />
          )}
        </button>

        {isExpanded && (
          <div className="flex flex-col gap-3 px-4 pb-4 text-sm text-gray-900">
            <p>{t("stripe_data_shared")}</p>
            <p>{t("stripe_review_data")}</p>
            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="w-fit text-blue-600 hover:underline"
            >
              {t("stripe_privacy_policy")}
            </a>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-500">
                {linkedDateText && t("stripe_linked_on", { date: linkedDateText })}
              </p>
              <div className="flex items-center gap-2">
                {isPending && accountInfo?.onboardingUrl && (
                  <Button
                    color="primary"
                    size="sm"
                    onPress={() => openLink(accountInfo.onboardingUrl as string)}
                  >
                    {t("update_details")}
                  </Button>
                )}
                <Button
                  size="sm"
                  radius="full"
                  variant="flat"
                  isDisabled={isUnlinking}
                  startContent={<img src={unlinkIcon} alt="unlink" className="size-4" />}
                  onPress={() => setIsUnlinkConfirmOpen(true)}
                >
                  {t("unlink")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 font-['Plus_Jakarta_Sans']">
      <div className="border-b border-gray-100 pb-3">
        <h3 className="text-lg font-bold text-gray-900">
          {t("link_account")}
        </h3>
      </div>

      <p className="text-sm text-gray-600">{t("link_account_description")}</p>

      {renderContent()}

      <ConfirmModal
        isOpen={isUnlinkConfirmOpen}
        onClose={() => setIsUnlinkConfirmOpen(false)}
        cardTitle={t("unlink_stripe_title")}
        cardDescription={t("unlink_stripe_description")}
        buttonText={t("unlink")}
        onConfirm={handleUnlink}
      />
    </div>
  );
};

export default StripeLinkCard;


